// Desktop three-panel row changes (open right/left, open centre, close): the panel
// row changes at once and every window glides from where it was to where it now
// sits (FLIP), while a window arriving in view fades in. Swiper keys virtual slides
// by index, so slot DOM is reused across a change; windows are matched by panel id
// (data-slide-panel-id on the slide wrapper; data-panel-id stays unique to the
// panel's own elements), never by slot.

export type PanelBox = { left: number; top: number; width: number; height: number }

export type PanelRowShift = {
    token: number
    // Where each visible window was just before the change, by panel id.
    boxes: Record<string, PanelBox>
    // A window in view afterwards with no old box (the one an insert added, or one
    // a close brought on screen) fades in, drifting from this side.
    enterFrom: 'left' | 'right'
}

export const PANEL_ROW_SHIFT_MS = 300
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'
// How far the entering window drifts while it fades in.
const ENTER_OFFSET_PX = 24
const WRAPPER_SELECTOR = '.panel-slide-wrapper[data-slide-panel-id]'
const CONTAINER_SELECTOR = '.panel-container-comp'

let nextToken = 0
export const nextPanelRowShiftToken = () => ++nextToken

const visibleBox = (element: Element): PanelBox | null => {
    const rect = element.getBoundingClientRect()
    if (!rect.width || rect.right <= 0 || rect.left >= window.innerWidth) return null
    return { left: rect.left, top: rect.top, width: rect.width, height: rect.height }
}

/** Measure every on-screen window by panel id. Call before the change commits. */
export function capturePanelRow(): Record<string, PanelBox> {
    const boxes: Record<string, PanelBox> = {}
    if (typeof document === 'undefined') return boxes
    document.querySelectorAll<HTMLElement>(WRAPPER_SELECTOR).forEach(wrapper => {
        const panelId = wrapper.dataset.slidePanelId
        const container = wrapper.querySelector(CONTAINER_SELECTOR)
        const box = container && visibleBox(container)
        if (panelId && box) boxes[panelId] = box
    })
    return boxes
}

type Running = { finish: () => void }
let running: Running | null = null

/**
 * Play a row shift against the committed layout, before it paints, so the windows
 * start from their old boxes on the first painted frame. Call it once the layout
 * has settled (after any synchronous slide the change triggers).
 */
export function playPanelRowShift(shift: PanelRowShift, onFinish: () => void) {
    running?.finish()
    if (typeof document === 'undefined') return onFinish()

    const wrappers = [...document.querySelectorAll<HTMLElement>(WRAPPER_SELECTOR)]
    const touched: { wrapper: HTMLElement; container: HTMLElement | null }[] = []

    // The windows' own side-scale transition would replay the change we are about
    // to animate and would hide their final boxes from measurement.
    wrappers.forEach(wrapper => {
        const container = wrapper.querySelector<HTMLElement>(CONTAINER_SELECTOR)
        if (container) container.style.transition = 'none'
    })

    wrappers.forEach(wrapper => {
        const panelId = wrapper.dataset.slidePanelId
        const container = wrapper.querySelector<HTMLElement>(CONTAINER_SELECTOR)
        if (!panelId || !container) return

        const from = shift.boxes[panelId]
        if (!from) {
            if (!visibleBox(container)) return
            const drift = shift.enterFrom === 'left' ? -ENTER_OFFSET_PX : ENTER_OFFSET_PX
            wrapper.style.transition = 'none'
            wrapper.style.opacity = '0'
            wrapper.style.transform = `translateX(${drift}px)`
            touched.push({ wrapper, container })
            return
        }

        const to = container.getBoundingClientRect()
        if (!to.width || !to.height) return
        const dx = from.left - to.left
        const dy = from.top - to.top
        const sx = from.width / to.width
        const sy = from.height / to.height
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(sx - 1) < 0.002) return

        // Scale about the window's new top-left corner, then move it back to where
        // it was: the window's painted box equals its old box on the first frame.
        const wrapperRect = wrapper.getBoundingClientRect()
        wrapper.style.transition = 'none'
        wrapper.style.transformOrigin = `${to.left - wrapperRect.left}px ${
            to.top - wrapperRect.top
        }px`
        wrapper.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`
        touched.push({ wrapper, container })
    })

    const restoreContainers = () => {
        wrappers.forEach(wrapper => {
            const container = wrapper.querySelector<HTMLElement>(CONTAINER_SELECTOR)
            if (container) container.style.transition = ''
        })
    }

    if (!touched.length) {
        restoreContainers()
        onFinish()
        return
    }

    // Commit the start frame, then release every window toward its new box together.
    void document.body.offsetWidth
    const transition = `transform ${PANEL_ROW_SHIFT_MS}ms ${EASING}, opacity ${PANEL_ROW_SHIFT_MS}ms ${EASING}`
    touched.forEach(({ wrapper }) => {
        wrapper.style.willChange = 'transform, opacity'
        wrapper.style.transition = transition
        wrapper.style.transform = ''
        wrapper.style.opacity = ''
    })

    let done = false
    const finish = () => {
        if (done) return
        done = true
        window.clearTimeout(timeout)
        touched.forEach(({ wrapper }) => {
            wrapper.style.transition = ''
            wrapper.style.transform = ''
            wrapper.style.transformOrigin = ''
            wrapper.style.opacity = ''
            wrapper.style.willChange = ''
        })
        restoreContainers()
        if (running === current) running = null
        onFinish()
    }
    const timeout = window.setTimeout(finish, PANEL_ROW_SHIFT_MS + 50)
    const current: Running = { finish }
    running = current
}
